// =====================================================================
// App.js — Multi-Branch Financial Dashboard (Excelsior Financial Dashboard)
// CodeSandbox-ready | All logic in one file | localStorage persistence
// Dependency required: recharts (add to package.json)
// =====================================================================

import React, { useState, useEffect, useRef, useCallback } from "react";
import {

  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { supabase } from './supabaseClient';
import LoginPage from './LoginPage';
import AdminPage from './AdminPage';
import ReportsTab from './ReportsTab';
import GovContribTab from './GovContribTab';
import { useAutosave } from './hooks/useAutosave';
// ─── CONSTANTS ─────────────────────────────────────────────────────────────


const EXPENSE_FIELDS = [
  { key: "mealAllowance",  label: "Meal Allowance",  isExpense: true  },
  { key: "transportation", label: "Transportation",  isExpense: true  },
  { key: "electricity",    label: "Electricity",     isExpense: false },
  { key: "internet",       label: "Internet",        isExpense: true  },
  { key: "salary",         label: "Salary",          isExpense: true  },
  { key: "sss",            label: "SSS Deduction",   isExpense: false },
  { key: "philhealth",     label: "PhilHealth",      isExpense: false },
  { key: "hdmf",           label: "HDMF",            isExpense: false },
  { key: "others",         label: "Others / Misc",   isExpense: true  },
];

const TABS = [
"Dashboard",
"Daily Entry",
"Employees",
"Gov't Contributions",
"Reports",
"Cash Flow",
"Backup & Restore"
];

// ─── SEED DATA ──────────────────────────────────────────────────────────────
function buildSeedRecords() {
  const records = [];
  const branchIds = ["b1", "b2"];
  const now = new Date();
  // 3 months of sample data
  for (let mo = 2; mo >= 0; mo--) {
    const d = new Date(now.getFullYear(), now.getMonth() - mo, 1);
    const yr = d.getFullYear();
    const mn = d.getMonth(); // 0-indexed
    const dim = new Date(yr, mn + 1, 0).getDate();
    branchIds.forEach((bid) => {
      for (let day = 1; day <= dim; day += 2) {
        const ds = `${yr}-${String(mn + 1).padStart(2, "0")}-${String(
          day
        ).padStart(2, "0")}`;
        const sales = Math.round(15000 + Math.random() * 25000);
        const cost = Math.round(sales * (0.35 + Math.random() * 0.1));
        records.push({
          id: `r_${bid}_${ds.replace(/-/g, "")}`,
          branchId: bid,
          date: ds,
          sales,
          cost,
          expenses: {
            mealAllowance: Math.round(600 + Math.random() * 400),
            transportation: Math.round(200 + Math.random() * 300),
            electricity: Math.round(250 + Math.random() * 200),
            internet: 50,
            salary: Math.round(1000 + Math.random() * 1000),
            sss: Math.round(150 + Math.random() * 100),
            philhealth: Math.round(75 + Math.random() * 50),
            hdmf: Math.round(40 + Math.random() * 30),
            others: Math.round(100 + Math.random() * 200),
          },
        });
      }
    });
  }
  return records;
}

const INITIAL_STATE = {
  branches: [
    { id: "main", name: "Main Company", isMain: true },
    { id: "b1", name: "Branch – Cebu", isMain: false },
    { id: "b2", name: "Branch – Davao", isMain: false },
  ],
  employees: [
    {
      id: "e1",
      name: "Juan Dela Cruz",
      branchId: "b1",
      dateHired: "2023-01-15",
      dateSeparated: "",
      status: "Active",
      monthlySalary: 18000,
      mealAllowance: 2000,
      otherAllowances: 500,
      sssDeduction: 900,
      philhealthDeduction: 450,
      hdmfDeduction: 200,
      otherDeductions: 0,
    },
    {
      id: "e2",
      name: "Maria Santos",
      branchId: "b2",
      dateHired: "2022-06-01",
      dateSeparated: "",
      status: "Active",
      monthlySalary: 22000,
      mealAllowance: 2500,
      otherAllowances: 1000,
      sssDeduction: 1100,
      philhealthDeduction: 550,
      hdmfDeduction: 200,
      otherDeductions: 0,
    },
    {
      id: "e3",
      name: "Pedro Reyes",
      branchId: "b1",
      dateHired: "2021-03-10",
      dateSeparated: "2024-11-30",
      status: "Inactive",
      monthlySalary: 15000,
      mealAllowance: 1500,
      otherAllowances: 0,
      sssDeduction: 750,
      philhealthDeduction: 375,
      hdmfDeduction: 200,
      otherDeductions: 0,
    },
  ],
  dailyRecords: buildSeedRecords(),
  lastBackup: null,
};

// ─── HELPERS ────────────────────────────────────────────────────────────────
// ─── GOVERNMENT CONTRIBUTION CALCULATORS ─────────────────────────────────────

function computeSSS(dailyRate) {
  const monthly = dailyRate * 26;
  // EC: 10 if monthly < 14750, 30 if monthly >= 14750
  const ec = monthly < 14750 ? 10 : 30;
  const brackets = [
    [5249.99,   250,  500],
    [5749.99,   275,  550],
    [6249.99,   300,  600],
    [6749.99,   325,  650],
    [7249.99,   350,  700],
    [7749.99,   375,  750],
    [8249.99,   400,  800],
    [8749.99,   425,  850],
    [9249.99,   450,  900],
    [9749.99,   475,  950],
    [10249.99,  500,  1000],
    [10749.99,  525,  1050],
    [11249.99,  550,  1100],
    [11749.99,  575,  1150],
    [12249.99,  600,  1200],
    [12749.99,  625,  1250],
    [13249.99,  650,  1300],
    [13749.99,  675,  1350],
    [14249.99,  700,  1400],
    [14749.99,  725,  1450],
    [15249.99,  750,  1500],
    [15749.99,  775,  1550],
    [16249.99,  800,  1600],
    [16749.99,  825,  1650],
    [17249.99,  850,  1700],
    [17749.99,  875,  1750],
    [18249.99,  900,  1800],
    [18749.99,  925,  1850],
    [19249.99,  950,  1900],
    [19749.99,  975,  1950],
    [20249.99,  1000, 2000],
    [20749.99,  1000, 2000],
    [21249.99,  1000, 2000],
    [21749.99,  1000, 2000],
    [22249.99,  1000, 2000],
    [22749.99,  1000, 2000],
    [23249.99,  1000, 2000],
    [23749.99,  1000, 2000],
    [24249.99,  1000, 2000],
    [24749.99,  1000, 2000],
    [25249.99,  1000, 2000],
    [25749.99,  1000, 2000],
    [26249.99,  1000, 2000],
    [26749.99,  1000, 2000],
    [27249.99,  1000, 2000],
    [27749.99,  1000, 2000],
    [28249.99,  1000, 2000],
    [28749.99,  1000, 2000],
    [29249.99,  1000, 2000],
    [29749.99,  1000, 2000],
    [30249.99,  1000, 2000],
    [30749.99,  1000, 2000],
    [31249.99,  1000, 2000],
    [31749.99,  1000, 2000],
    [32249.99,  1000, 2000],
    [32749.99,  1000, 2000],
    [33249.99,  1000, 2000],
    [33749.99,  1000, 2000],
    [34249.99,  1000, 2000],
    [34749.99,  1000, 2000],
    [Infinity,  1000, 2000],
  ];
  for (const [limit, emp, emr] of brackets) {
    if (monthly <= limit) return { employee: emp, employer: emr + ec };
  }
  return { employee: 1000, employer: 2000 + ec };
}

function computePhilHealth(dailyRate) {
  const monthly = dailyRate * 26;
  if (monthly <= 10000) return { employee: 250, employer: 250 };
  if (monthly >= 100000) return { employee: 2500, employer: 2500 };
  const total = monthly * 0.05;
  return { employee: total / 2, employer: total / 2 };
}

function computeHDMF(dailyRate) {
  const monthly = dailyRate * 26;
  const basis = Math.min(monthly, 10000);
  const empRate = basis <= 1500 ? 0.01 : 0.02;
  const emrRate = 0.02;
  return {
    employee: Math.min(basis * empRate, 200),
    employer: Math.min(basis * emrRate, 200),
  };
}

function computeAllContributions(dailyRate) {
  const sss        = computeSSS(dailyRate);
  const philhealth = computePhilHealth(dailyRate);
  const hdmf       = computeHDMF(dailyRate);
  return {
    sssEmployee:        sss.employee,
   sssEmployer: sss.employer, // EC already included in computeSSS
    philhealthEmployee: Math.round(philhealth.employee * 100) / 100,
    philhealthEmployer: Math.round(philhealth.employer * 100) / 100,
    hdmfEmployee:       Math.round(hdmf.employee * 100) / 100,
    hdmfEmployer:       Math.round(hdmf.employer * 100) / 100,
  };
}
const fmt = (n) =>
  `₱${Number(n || 0).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const pct = (n) => `${Number(n || 0).toFixed(2)}%`;
const sumExp = (exp) =>
  EXPENSE_FIELDS.filter(f => f.isExpense).reduce((s, f) => s + Number(exp?.[f.key] || 0), 0);
const genId = () =>
  `id_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
const toMonthKey = (ds) => (ds || "").slice(0, 7);
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  return new Date(y, m - 1).toLocaleString("en", {
    month: "short",
     year: "numeric",
  });
};

/** Filter records by branch list + optional date range */
function filterRecords(records, branchIds, from, to) {
  return records.filter(
    (r) =>
      branchIds.includes(r.branchId) &&
      (!from || r.date >= from) &&
      (!to || r.date <= to)
  );
}

/** Group records into monthly buckets for charts */
function aggregateByMonth(records) {
  const map = {};
  records.forEach((r) => {
    const k = toMonthKey(r.date);
    if (!map[k]) map[k] = { key: k, sales: 0, cost: 0, expenses: 0, profit: 0 };
    map[k].sales += Number(r.sales || 0);
    map[k].cost += Number(r.cost || 0);
    map[k].expenses += sumExp(r.expenses);
  });
  Object.values(map).forEach((m) => {
    m.profit = m.sales - m.cost - m.expenses;
  });
  return Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
}

/** Compute KPI totals for a record set */
function calcSummary(records) {
  const totalSales = records.reduce((s, r) => s + Number(r.sales || 0), 0);
  const totalCost = records.reduce((s, r) => s + Number(r.cost || 0), 0);
  const totalExpenses = records.reduce((s, r) => s + sumExp(r.expenses), 0);
  const netProfit = totalSales - totalCost - totalExpenses;
  const opExpRatio = totalSales > 0 ? (totalExpenses / totalSales) * 100 : 0;
  const grossMargin =
    totalSales > 0 ? ((totalSales - totalCost) / totalSales) * 100 : 0;
  return {
    totalSales,
    totalCost,
    totalExpenses,
    netProfit,
    opExpRatio,
    grossMargin,
  };
}

/** Convert array of objects to CSV string */
function objToCSV(rows, fields) {
  const header = fields.map((f) => f.label).join(",");
  const body = rows
    .map((r) => fields.map((f) => JSON.stringify(r[f.key] ?? "")).join(","))
    .join("\n");
  return `${header}\n${body}`;
}

/** Trigger browser file download */
function downloadFile(content, filename, type = "application/json") {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Parse CSV text into { headers, rows } */
function parseCSVRows(text) {
  const lines = text.trim().split("\n");
  const headers = lines[0]
    .split(",")
    .map((h) => h.trim().replace(/^"|"$/g, ""));
  const rows = lines.slice(1).map((line) => {
    const vals = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
    return headers.reduce((obj, h, i) => {
      obj[h] = vals[i] || "";
      return obj;
    }, {});
  });
  return { headers, rows };
}

// ─── APP ROOT ────────────────────────────────────────────────────────────────
export default function App() {
  const [state, setState] = useState(null); // null = loading
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [selectedBranchId, setSelectedBranchId] = useState("main");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showBranchMgr, setShowBranchMgr] = useState(false);
  const [session, setSession] = useState(null);
  const [branchLimit, setBranchLimit] = useState(999);
  const [showAdmin, setShowAdmin] = useState(false);
  
 useEffect(() => {
  supabase.auth.getSession().then(({ data: { session } }) => {
    setSession(session);
  });
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    (_event, session) => setSession(session)
  );
  return () => subscription.unsubscribe();
}, []);
  // ── Load from Supabase on mount
  useEffect(() => {
  if (!session) return;
  async function loadSettings() {
    const { data } = await supabase
      .from('user_settings')
      .select('branch_limit')
      .eq('user_id', session.user.id)
      .single();

    if (data) {
      setBranchLimit(data.branch_limit);
    } else {
      // Auto-create record for new user with Starter plan (1 branch)
      await supabase
        .from('user_settings')
        .insert({
          user_id: session.user.id,
          email: session.user.email,
          plan_name: 'Starter',
          branch_limit: 1,
        });
      setBranchLimit(1);
    }
  }
  loadSettings();
}, [session]);
useEffect(() => {
  if (!session) return;
  async function loadData() {
    const { data, error } = await supabase
      .from('dashboard_data')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    if (error || !data) {
      setState(INITIAL_STATE);
    } else {
      setState({
        branches: data.branches,
        employees: data.employees,
        dailyRecords: data.daily_records,
        lastBackup: data.last_saved,
      });
    }
  }
  loadData();
}, [session]);
// Auto logout after 30 minutes of inactivity
useEffect(() => {
  if (!session) return;
  let timer;

  function resetTimer() {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      await supabase.auth.signOut();
      alert('You have been signed out due to inactivity.');
    }, 30 * 60 * 1000);
  }

  const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
  events.forEach(e => window.addEventListener(e, resetTimer));
  resetTimer();

  return () => {
    clearTimeout(timer);
    events.forEach(e => window.removeEventListener(e, resetTimer));
  };
}, [session]);
 const [savingStatus, setSavingStatus] = React.useState('idle') 
// Warn before browser close if there are unsaved changes
useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (savingStatus === 'saving') {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      return e.returnValue;
    }
  };
  window.addEventListener('beforeunload', handleBeforeUnload);
  return () => window.removeEventListener('beforeunload', handleBeforeUnload);
}, [savingStatus]);

     
 // ── Auto-save to Supabase on every state change
useEffect(() => {
  if (!state || !session) return;
  async function saveData() {
    await supabase
      .from('dashboard_data')
      .upsert({
        user_id: session.user.id,
        branches: state.branches,
        employees: state.employees,
        daily_records: state.dailyRecords,
        last_saved: new Date().toISOString(),
      }, { onConflict: 'user_id' });
  }
  saveData();
}, [state]);

  /** Immutable state updater — stamps lastBackup timestamp */
  const updateState = useCallback((updater) => {
    setState((prev) => {
      const next =
        typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      return { ...next, lastBackup: new Date().toISOString() };
    });
  }, []);
if (!session)
  return <LoginPage />;
    if (showAdmin)
  return <AdminPage session={session} onBack={() => setShowAdmin(false)} />;
  
    if (!state)
    return (
      <div style={{ padding: 40, textAlign: "center", fontSize: 18 }}>
        ⏳ Loading…
      </div>
    );

  return (
   <div style={S.layout}>

<div
style={{
...S.sidebar,
width: sidebarOpen ? 210 : 0,
padding: sidebarOpen ? "18px 14px" : "0px",
overflow: "hidden",
transition: "all 0.25s ease"
}}
>

<div style={S.sidebarTitle}>
Excelsior Dashboard
</div>

<div
style={S.menuItem}
onMouseEnter={(e)=>e.currentTarget.style.background="#334155"}
onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
onClick={()=>setActiveTab("Dashboard")}
>
Dashboard
</div>

<div
style={S.menuItem}
onMouseEnter={(e)=>e.currentTarget.style.background="#334155"}
onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
onClick={()=>setActiveTab("Daily Entry")}
>
Daily Entry
</div>

<div
style={S.menuItem}
onMouseEnter={(e)=>e.currentTarget.style.background="#334155"}
onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
onClick={()=>setActiveTab("Employees")}
>
Employees
</div>

<div
style={S.menuItem}
onMouseEnter={(e)=>e.currentTarget.style.background="#334155"}
onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
onClick={()=>setActiveTab("Gov't Contributions")}
>
Gov't Contributions
</div>

<div
style={S.menuItem}
onMouseEnter={(e)=>e.currentTarget.style.background="#334155"}
onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
onClick={()=>setActiveTab("Reports")}
>
Reports
</div>

<div
style={S.menuItem}
onMouseEnter={(e)=>e.currentTarget.style.background="#334155"}
onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
onClick={()=>setActiveTab("Cash Flow")}
>
Cash Flow
</div>
  
<div
style={S.menuItem}
onMouseEnter={(e)=>e.currentTarget.style.background="#334155"}
onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
onClick={()=>setActiveTab("Backup & Restore")}
>
Backup
</div>

</div>

<div style={S.content}>
      {/* ── Header ── */}
      <header style={S.header}>
        <div style={S.headerLeft}>
  <button
style={S.menuToggle}
onMouseEnter={(e)=>e.currentTarget.style.background="#475569"}
onMouseLeave={(e)=>e.currentTarget.style.background="#334155"}
onClick={()=>setSidebarOpen(!sidebarOpen)}
>
☰
</button>

          <span style={S.logo}>📊 Excelsior Financial Dashboard</span>
          <select
            value={selectedBranchId}
            onChange={(e) => setSelectedBranchId(e.target.value)}
            style={S.branchSel}
          >
            {state.branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.isMain ? "🏢 " : "🏪 "}
                {b.name}
              </option>
            ))}
          </select>
          <button onClick={() => setShowBranchMgr(true)} style={S.btnSmall}>
            ⚙ Branches
          </button>
        </div>
        <div style={S.headerRight}>
         {savingStatus === 'saving' && (
            <span style={{ ...S.savedBadge, background: '#92400e', color: '#fef3c7' }}>
              💾 Saving...
            </span>
          )}
          {savingStatus === 'saved' && (
            <span style={{ ...S.savedBadge, background: '#065f46', color: '#d1fae5' }}>
              ✅ Saved
            </span>
          )}
          {savingStatus === 'idle' && state.lastBackup && (
            <span style={S.savedBadge}>
              💾 {new Date(state.lastBackup).toLocaleTimeString()}
            </span>
          )}
{session.user.id === 'cac6dfc1-bced-48fa-b920-3d17147bfd06' && (
  <button
    onClick={() => setShowAdmin(true)}
    style={{ ...S.btnSmall, background: '#1e1b4b', marginRight: 8 }}
  >
    🛡️ Admin
  </button>
)}
<button
  onClick={async () => await supabase.auth.signOut()}
  style={{ ...S.btnSmall, background: '#dc2626' }}
>
  Sign Out
</button>
        </div>
      </header>

      {/* ── Nav Tabs ── */}
      <nav style={S.nav}>
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              ...S.navBtn,
              ...(activeTab === tab ? S.navBtnActive : {}),
            }}
          >
            {tab}
          </button>
        ))}
      </nav>

      {/* ── Page Content ── */}
      <main style={S.main}>
        {activeTab === "Dashboard" && (
          <DashboardTab state={state} selectedBranchId={selectedBranchId} />
        )}
        {activeTab === "Daily Entry" && (
          <DailyEntryTab
            state={state}
            updateState={updateState}
            selectedBranchId={selectedBranchId}
          />
        )}
       {activeTab === "Employees" && (
          <EmployeesTab
            state={state}
            updateState={updateState}
            selectedBranchId={selectedBranchId}
            setSavingStatus={setSavingStatus}
          />
        )}
         {activeTab === "Gov't Contributions" && (
  <GovContribTab
    state={state}
    updateState={updateState}
    selectedBranchId={selectedBranchId}
  />
)}
         {activeTab === "Reports" && (
  <ReportsTab
    state={state}
    selectedBranchId={selectedBranchId}
  />
)}
        {activeTab === "Cash Flow" && (
  <CashFlowTab
    state={state}
    selectedBranchId={selectedBranchId}
  />
)}  
        {activeTab === "Backup & Restore" && (
          <BackupRestoreTab
            state={state}
            updateState={updateState}
            setState={setState}
          />
        )}
   
      </main>

{/* ── Branch Manager Modal ── */}
{showBranchMgr && (
  <BranchManager
    state={state}
    updateState={updateState}
    onClose={() => setShowBranchMgr(false)}
    setSelectedBranchId={setSelectedBranchId}
    branchLimit={branchLimit}
  />
)}

</div>
</div>
);
}
         function CashFlowTab({ state, selectedBranchId }) {
           const now = new Date();
const [fromDate, setFromDate] = useState(
  `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`
);
const [toDate, setToDate] = useState(now.toISOString().slice(0,10));

  const isMain = selectedBranchId === "main";

  const branchIds = isMain
    ? state.branches.filter(b => !b.isMain).map(b => b.id)
    : [selectedBranchId];

  const records = state.dailyRecords.filter(
  r =>
    branchIds.includes(r.branchId) &&
    (!fromDate || r.date >= fromDate) &&
    
    (!toDate || r.date <= toDate)
);
  const cashIn = records.reduce((s,r)=> s + Number(r.sales || 0),0);
  const costOut = records.reduce((s,r)=> s + Number(r.cost || 0),0);
  const expenseOut = records.reduce((s,r)=> s + sumExp(r.expenses),0);

  const cashOut = costOut + expenseOut;
const netCashFlow = cashIn - cashOut;
const monthly = aggregateByMonth(records);

function exportCashFlow(){

const rows = [
["Item","Amount"],
["Sales",cashIn],
["Cost of Sales",costOut],
["Operating Expenses",expenseOut],
["Total Cash Out",cashOut],
["Net Cash Flow",netCashFlow],
["Ending Cash Balance",netCashFlow]
];

let csv = rows.map(r=>r.join(",")).join("\n");

const blob = new Blob([csv],{type:"text/csv"});
const url = URL.createObjectURL(blob);

const a = document.createElement("a");
a.href = url;
a.download = "cashflow-report.csv";
a.click();

URL.revokeObjectURL(url);

}
  const totalSales = monthly.reduce((s,m)=> s + m.sales,0);
  const totalOut = monthly.reduce((s,m)=> s + (m.cost + m.expenses),0);
  const totalNet = monthly.reduce((s,m)=> s + m.profit,0);
  return (
  <div style={S.card}>
         <div style={S.filterRow}>
  <label style={S.label}>From</label>
  <input
    type="date"
    value={fromDate}
    onChange={(e)=>setFromDate(e.target.value)}
    style={S.input}
  />

  <label style={S.label}>To</label>
  <input
    type="date"
    value={toDate}
    onChange={(e)=>setToDate(e.target.value)}
    style={S.input}
  />
</div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>

<h3 style={{margin:0,fontSize:16,fontWeight:700,color:"#1f2937"}}>
Cash Flow Summary
</h3>

<button
style={{ ...S.btnOutline, marginBottom: 12 }}
onClick={exportCashFlow}
>
⬇ Export CSV
</button>

</div>
        <div style={S.kpiGrid}>
  <KpiCard
    label="Operating Cash Flow"
    value={fmt(netCashFlow)}
    color={netCashFlow >= 0 ? "#16a34a" : "#dc2626"}
    icon="💵"
  />
</div>

    <table style={S.table}>
      <tbody>

        <tr>
          <td style={S.td}><strong>Cash In</strong></td>
          <td style={S.td}></td>
        </tr>

        <tr>
          <td style={S.td}>Sales</td>
          <td style={S.td}>{fmt(cashIn)}</td>
        </tr>

        <tr>
          <td style={S.td}><strong>Cash Out</strong></td>
          <td style={S.td}></td>
        </tr>

        <tr>
          <td style={S.td}>Cost of Sales</td>
          <td style={S.td}>{fmt(costOut)}</td>
        </tr>

        <tr>
          <td style={S.td}>Operating Expenses</td>
          <td style={S.td}>{fmt(expenseOut)}</td>
        </tr>

        <tr>
          <td style={{...S.td,fontWeight:700}}>Total Cash Out</td>
          <td style={{...S.td,fontWeight:700}}>{fmt(cashOut)}</td>
        </tr>

        <tr>
          <td style={{...S.td,fontWeight:700,fontSize:16}}>Net Cash Flow</td>
          <td style={{...S.td,fontWeight:700,fontSize:16}}>
            {fmt(netCashFlow)}
          </td>
        </tr>
       <tr>
  <td style={{...S.td,fontWeight:700}}>Ending Cash Balance</td>
  <td style={{...S.td,fontWeight:700}}>
    {fmt(netCashFlow)}
  </td>
</tr>
    
      </tbody>
    </table>
<div style={S.card}>

<h3 style={S.cardTitle}>Monthly Cash Flow</h3>

<table style={S.table}>

<thead>
<tr>
<th style={S.th}>Month</th>
<th style={S.th}>Cash In</th>
<th style={S.th}>Cash Out</th>
<th style={S.th}>Net</th>
</tr>
</thead>

<tbody>

{monthly.map(m => (

<tr key={m.key}>
<td style={S.td}>{monthLabel(m.key)}</td>
<td style={S.td}>{fmt(m.sales)}</td>
<td style={S.td}>{fmt(m.cost + m.expenses)}</td>
<td
  style={{
    ...S.td,
    color: m.profit >= 0 ? "#16a34a" : "#dc2626",
    fontWeight: 600
  }}
>
  {fmt(m.profit)}
</td>
</tr>

))}
<tr style={{background:"#f3f4f6"}}>
<td style={{...S.td,fontWeight:700}}>Total</td>

<td style={{...S.td,fontWeight:700}}>
{fmt(totalSales)}
</td>

<td style={{...S.td,fontWeight:700}}>
{fmt(totalOut)}
</td>

<td style={{
...S.td,
fontWeight:700,
color: totalNet >= 0 ? "#16a34a" : "#dc2626"
}}>
{fmt(totalNet)}
</td>

</tr>
</tbody>

</table>

</div>
  </div>
);
}
// ─── DASHBOARD TAB ───────────────────────────────────────────────────────────
function DashboardTab({ state, selectedBranchId }) {
  const now = new Date();
  const [fromDate, setFromDate] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`
  );
  const [toDate, setToDate] = useState(now.toISOString().slice(0, 10));

  const isMain = selectedBranchId === "main";
  const branchIds = isMain
    ? state.branches.filter((b) => !b.isMain).map((b) => b.id)
    : [selectedBranchId];

  const filtered = filterRecords(
    state.dailyRecords,
    branchIds,
    fromDate,
    toDate
  );
  const summary = calcSummary(filtered);
  const monthly = aggregateByMonth(filtered);

  const chartData = monthly.map((m) => ({
    name: monthLabel(m.key),
    Sales: m.sales,
    Cost: m.cost,
    Expenses: m.expenses,
    "Net Profit": m.profit,
  }));

  // Branch comparison (Main view only)
  const branchCompare = isMain
    ? state.branches
        .filter((b) => !b.isMain)
        .map((b) => {
          const recs = filterRecords(
            state.dailyRecords,
            [b.id],
            fromDate,
            toDate
          );
          const s = calcSummary(recs);
          return {
            name: b.name,
            Sales: s.totalSales,
            Profit: s.netProfit,
            Expenses: s.totalExpenses,
          };
        })
    : [];

  return (
    <div>
      {/* Date range filter */}
      <div style={S.filterRow}>
        <label style={S.label}>From</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={S.input}
        />
        <label style={S.label}>To</label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={S.input}
        />
      </div>

      {/* KPI Cards */}
      <div style={S.kpiGrid}>
        <KpiCard
          label="Total Gross Sales"
          value={fmt(summary.totalSales)}
          color="#4f46e5"
          icon="💰"
        />
        <KpiCard
          label="Total Cost"
          value={fmt(summary.totalCost)}
          color="#0891b2"
          icon="🏷️"
        />
        <KpiCard
          label="Total Expenses"
          value={fmt(summary.totalExpenses)}
          color="#d97706"
          icon="📋"
        />
        <KpiCard
          label="Net Profit / Loss"
          value={fmt(summary.netProfit)}
          color={summary.netProfit >= 0 ? "#16a34a" : "#dc2626"}
          icon={summary.netProfit >= 0 ? "📈" : "📉"}
        />
        <KpiCard
          label="Gross Margin"
          value={pct(summary.grossMargin)}
          color="#7c3aed"
          icon="%"
        />
        <KpiCard
          label="Op. Expense Ratio"
          value={pct(summary.opExpRatio)}
          color="#db2777"
          icon="⚖️"
        />
      </div>

      {/* Sales & Profit Trend */}
      <div style={S.chartCard}>
        <h3 style={S.chartTitle}>📈 Monthly Sales & Profit Trend</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
            <Line
              type="monotone"
              dataKey="Sales"
              stroke="#4f46e5"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="Net Profit"
              stroke="#16a34a"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Expense Trend */}
      <div style={S.chartCard}>
        <h3 style={S.chartTitle}>📋 Monthly Expense Trend</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
            <Bar dataKey="Cost" fill="#0891b2" />
            <Bar dataKey="Expenses" fill="#d97706" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Branch Comparison (Main only) */}
      {isMain && branchCompare.length > 0 && (
        <div style={S.chartCard}>
          <h3 style={S.chartTitle}>🏪 Branch Comparison</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={branchCompare}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend />
              <Bar dataKey="Sales" fill="#4f46e5" />
              <Bar dataKey="Profit" fill="#16a34a" />
              <Bar dataKey="Expenses" fill="#d97706" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/** Reusable KPI card */
function KpiCard({ label, value, color, icon }) {
  return (
    <div style={{ ...S.kpiCard, borderTop: `4px solid ${color}` }}>
      <div style={S.kpiIcon}>{icon}</div>
      <div style={S.kpiLabel}>{label}</div>
      <div style={{ ...S.kpiValue, color }}>{value}</div>
    </div>
  );
}
// Input validation helper
function validateRecord(form) {
  const errors = [];
  if (!form.branchId)                                errors.push('Branch is required');
  if (!form.date)                                    errors.push('Date is required');
  if (isNaN(Number(form.sales)) || Number(form.sales) < 0)
                                                     errors.push('Sales must be a positive number');
  if (isNaN(Number(form.cost))  || Number(form.cost)  < 0)
                                                     errors.push('Cost must be a positive number');
  if (Number(form.sales) > 10000000)                 errors.push('Sales value seems too large');
  if (typeof form.notes === 'string' && form.notes.length > 500)
                                                     errors.push('Notes cannot exceed 500 characters');
  return errors;
  }


// ─── DAILY ENTRY TAB ─────────────────────────────────────────────────────────
function DailyEntryTab({ state, updateState, selectedBranchId }) {
  const isMain = selectedBranchId === "main";
  const availBranches = state.branches.filter((b) => !b.isMain);
  const defaultBid = isMain ? availBranches[0]?.id || "" : selectedBranchId;

  const emptyForm = () => ({
    branchId: defaultBid,
    date: new Date().toISOString().slice(0, 10),
    sales: "",
    cost: "",
    expenses: EXPENSE_FIELDS.reduce((o, f) => ({ ...o, [f.key]: "" }), {}),
  });

  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [showExp, setShowExp] = useState(false);
  const [filterBranch, setFilterBranch] = useState(
    isMain ? "all" : selectedBranchId
  );
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isMain) setFilterBranch(selectedBranchId);
  }, [selectedBranchId, isMain]);

  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));
  const setE = (key, val) =>
    setForm((f) => ({ ...f, expenses: { ...f.expenses, [key]: val } }));

  function handleSubmit() {
    setError("");
    const validationErrors = validateRecord(form);
if (validationErrors.length > 0) return setError(validationErrors.join('. '));

// Warning if sales is 0 but still allow saving
if (Number(form.sales) === 0) {
  const proceed = window.confirm('⚠️ Gross Sales is ₱0.00. Is this correct? Click OK to save anyway or Cancel to go back.');
  if (!proceed) return;
}
    // Prevent duplicate date per branch
    const dup = state.dailyRecords.some(
      (r) =>
        r.branchId === form.branchId && r.date === form.date && r.id !== editId
    );
    if (dup)
      return setError(
        `A record for ${form.date} already exists for this branch.`
      );

    const record = {
      id: editId || genId(),
      branchId: form.branchId,
      date: form.date,
      sales: Number(form.sales),
      cost: Number(form.cost || 0),
      expenses: EXPENSE_FIELDS.reduce(
        (o, f) => ({ ...o, [f.key]: Number(form.expenses[f.key] || 0) }),
        {}
      ),
    };
    updateState((prev) => ({
      ...prev,
      dailyRecords: editId
        ? prev.dailyRecords.map((r) => (r.id === editId ? record : r))
        : [...prev.dailyRecords, record],
    }));
    setForm(emptyForm());
    setEditId(null);
  }

  function startEdit(r) {
    setForm({
      branchId: r.branchId,
      date: r.date,
      sales: r.sales,
      cost: r.cost,
      expenses: { ...r.expenses },
    });
    setEditId(r.id);
    setShowExp(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleDelete(id) {
    if (!window.confirm("Delete this record?")) return;
    updateState((prev) => ({
      ...prev,
      dailyRecords: prev.dailyRecords.filter((r) => r.id !== id),
    }));
  }

  const displayRecords = state.dailyRecords
    .filter((r) => filterBranch === "all" || r.branchId === filterBranch)
    .sort((a, b) => b.date.localeCompare(a.date));

  const totalExpForm = sumExp(form.expenses);

  return (
    <div>
      {/* ── Entry Form ── */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>
          {editId ? "✏️ Edit Record" : "➕ New Daily Entry"}
        </h3>
        {error && <div style={S.errorMsg}>{error}</div>}

        <div style={S.formGrid}>
          <div style={S.formGroup}>
            <label style={S.label}>Branch *</label>
            <select
              value={form.branchId}
              onChange={(e) => set("branchId", e.target.value)}
              style={S.input}
            >
              <option value="">— Select Branch —</option>
              {availBranches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Date *</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => set("date", e.target.value)}
              style={S.input}
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Gross Sales (₱) *</label>
            <input
              type="number"
              value={form.sales}
              onChange={(e) => set("sales", e.target.value)}
              style={S.input}
              placeholder="0.00"
              min="0"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Daily Cost (₱)</label>
            <input
              type="number"
              value={form.cost}
              onChange={(e) => set("cost", e.target.value)}
              style={S.input}
              placeholder="0.00"
              min="0"
            />
          </div>
        </div>

        {/* Expandable expense breakdown */}
        <button
          onClick={() => setShowExp((v) => !v)}
          style={{ ...S.btnOutline, marginBottom: 12 }}
        >
          {showExp ? "▲ Hide" : "▼ Show"} Expense Breakdown
          {totalExpForm > 0 && ` (Total: ${fmt(totalExpForm)})`}
        </button>

        {showExp && (
          <div style={S.formGrid}>
            {EXPENSE_FIELDS.map((f) => (
              <div key={f.key} style={S.formGroup}>
                <label style={S.label}>{f.label} (₱)</label>
                <input
                  type="number"
                  value={form.expenses[f.key]}
                  onChange={(e) => setE(f.key, e.target.value)}
                  style={S.input}
                  placeholder="0.00"
                  min="0"
                />
              </div>
            ))}
            <div
              style={{ gridColumn: "1/-1", fontWeight: 700, color: "#374151" }}
            >
              Total Expenses: {fmt(totalExpForm)}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={handleSubmit} style={S.btnPrimary}>
            {editId ? "💾 Update" : "➕ Add Record"}
          </button>
          {editId && (
            <button
              onClick={() => {
                setForm(emptyForm());
                setEditId(null);
              }}
              style={S.btnSecondary}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* ── Records Table ── */}
      <div style={S.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <h3 style={{ ...S.cardTitle, margin: 0 }}>📋 Daily Records</h3>
          <select
            value={filterBranch}
            onChange={(e) => setFilterBranch(e.target.value)}
            style={S.input}
          >
            <option value="all">All Branches</option>
            {availBranches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {[
                  "Date",
                  "Branch",
                  "Sales",
                  "Cost",
                  "Expenses",
                  "Net",
                  "Actions",
                ].map((h) => (
                  <th key={h} style={S.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRecords.slice(0, 60).map((r) => {
                const exp = sumExp(r.expenses);
                const net = r.sales - r.cost - exp;
                const branch = state.branches.find((b) => b.id === r.branchId);
                return (
                  <tr key={r.id} style={S.tr}>
                    <td style={S.td}>{r.date}</td>
                    <td style={S.td}>{branch?.name || r.branchId}</td>
                    <td style={S.td}>{fmt(r.sales)}</td>
                    <td style={S.td}>{fmt(r.cost)}</td>
                    <td style={S.td}>{fmt(exp)}</td>
                    <td
                      style={{
                        ...S.td,
                        color: net >= 0 ? "#16a34a" : "#dc2626",
                        fontWeight: 600,
                      }}
                    >
                      {fmt(net)}
                    </td>
                    <td style={S.td}>
                      <button onClick={() => startEdit(r)} style={S.btnTiny}>
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        style={{
                          ...S.btnTiny,
                          marginLeft: 4,
                          background: "#fee2e2",
                          color: "#dc2626",
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
              {displayRecords.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    style={{ ...S.td, textAlign: "center", color: "#9ca3af" }}
                  >
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {displayRecords.length > 60 && (
            <p style={{ color: "#9ca3af", fontSize: 13, marginTop: 8 }}>
              Showing 60 of {displayRecords.length} records.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── EMPLOYEES TAB ───────────────────────────────────────────────────────────
function EmployeesTab({ state, updateState, selectedBranchId, setSavingStatus }) {
  const isMain = selectedBranchId === "main";
  const availBranches = state.branches.filter((b) => !b.isMain);

  const emptyEmp = () => ({
    name: "",
    branchId: isMain ? availBranches[0]?.id || "" : selectedBranchId,
    dateHired: "",
    dateSeparated: "",
    status: "Active",
    monthlySalary: "",
    mealAllowance: "",
    otherAllowances: "",
    otherDeductions: "",
    payrollSchedule: "Semi-monthly",
    govContribEnabled: true,
    sssEmployee: "",
    sssEmployer: "",
    philhealthEmployee: "",
    philhealthEmployer: "",
    hdmfEmployee: "",
    hdmfEmployer: "",
  });

  const [form, setForm] = useState(emptyEmp());
  const [editId, setEditId] = useState(null);
  const [filterBranch, setFilterBranch] = useState(
    isMain ? "all" : selectedBranchId
  );
  const [filterStatus, setFilterStatus] = useState("all");
  const [error, setError] = useState("");
  const [manualOverrides, setManualOverrides] = useState({});
  const [autosaveForm, setAutosaveForm]       = useState(null);

 const CONTRIB_FIELDS = [
  'sssEmployee','sssEmployer',
  'philhealthEmployee','philhealthEmployer',
  'hdmfEmployee','hdmfEmployer'
];

const setF = (field, val) => {
  // Track manual overrides for contribution fields
  if (CONTRIB_FIELDS.includes(field)) {
    setManualOverrides(prev => ({ ...prev, [field]: true }));
  }

  setForm(f => {
    const updated = { ...f, [field]: val };

    if (field === 'monthlySalary' && Number(val) > 0 && f.govContribEnabled !== false) {
      const computed = computeAllContributions(Number(val));
      // Only auto-fill fields that haven't been manually overridden
      const safeComputed = Object.fromEntries(
        Object.entries(computed).filter(([k]) => !manualOverrides[k])
      );
      return { ...updated, ...safeComputed };
    }
    if (field === 'govContribEnabled' && val === false) {
      return {
        ...updated,
        sssEmployee: 0, sssEmployer: 0,
        philhealthEmployee: 0, philhealthEmployer: 0,
        hdmfEmployee: 0, hdmfEmployer: 0,
      };
    }
    if (field === 'govContribEnabled' && val === true && Number(f.monthlySalary) > 0) {
      const computed = computeAllContributions(Number(f.monthlySalary));
      return { ...updated, ...computed };
    }
    return updated;
  });
};
  // Autosave — fires 2 seconds after user stops editing (edit mode only)
useAutosave(
  editId ? form : null,
  async (currentForm) => {
    if (!editId || !currentForm) return;
    setSavingStatus('saving');
    const emp = {
      id: editId,
      name: currentForm.name,
      branchId: currentForm.branchId,
      dateHired: currentForm.dateHired,
      dateSeparated: currentForm.dateSeparated,
      status: currentForm.status,
      monthlySalary:      Number(currentForm.monthlySalary      || 0),
      mealAllowance:      Number(currentForm.mealAllowance      || 0),
      otherAllowances:    Number(currentForm.otherAllowances    || 0),
      otherDeductions:    Number(currentForm.otherDeductions    || 0),
      payrollSchedule:    currentForm.payrollSchedule || 'Semi-monthly',
      govContribEnabled:  currentForm.govContribEnabled !== false,
      sssEmployee:        Number(currentForm.sssEmployee        || 0),
      sssEmployer:        Number(currentForm.sssEmployer        || 0),
      philhealthEmployee: Number(currentForm.philhealthEmployee || 0),
      philhealthEmployer: Number(currentForm.philhealthEmployer || 0),
      hdmfEmployee:       Number(currentForm.hdmfEmployee       || 0),
      hdmfEmployer:       Number(currentForm.hdmfEmployer       || 0),
      updated_at: new Date().toISOString(),
    };
    updateState(prev => ({
      ...prev,
      employees: prev.employees.map(e => e.id === editId ? emp : e),
    }));
    setTimeout(() => setSavingStatus('saved'), 500);
    setTimeout(() => setSavingStatus('idle'), 3000);
  },
  2000
);


function handleSubmit() {
    setError("");
   if (!form.name)        return setError('Full Name is required.');
if (!form.branchId)    return setError('Branch is required.');
if (!form.dateHired)   return setError('Date Hired is required.');
if (!form.monthlySalary || Number(form.monthlySalary) <= 0) 
                       return setError('Daily Salary is required and must be greater than zero.');

    const emp = {
      id: editId || genId(),
      name: form.name,
      branchId: form.branchId,
      dateHired: form.dateHired,
      dateSeparated: form.dateSeparated,
      status: form.status,
      monthlySalary: Number(form.monthlySalary || 0),
      mealAllowance: Number(form.mealAllowance || 0),
      otherAllowances: Number(form.otherAllowances || 0),
      otherDeductions: Number(form.otherDeductions || 0),
      payrollSchedule: form.payrollSchedule || 'Semi-monthly',
      govContribEnabled: form.govContribEnabled !== false,
      sssEmployee: form.govContribEnabled !== false ? Number(form.sssEmployee || 0) : 0,
      sssEmployer: form.govContribEnabled !== false ? Number(form.sssEmployer || 0) : 0,
      philhealthEmployee: form.govContribEnabled !== false ? Number(form.philhealthEmployee || 0) : 0,
      philhealthEmployer: form.govContribEnabled !== false ? Number(form.philhealthEmployer || 0) : 0,
      hdmfEmployee: form.govContribEnabled !== false ? Number(form.hdmfEmployee || 0) : 0,
      hdmfEmployer: form.govContribEnabled !== false ? Number(form.hdmfEmployer || 0) : 0,
    };
    updateState((prev) => ({
      ...prev,
      employees: editId
        ? prev.employees.map((e) => (e.id === editId ? emp : e))
        : [...prev.employees, emp],
    }));
    setForm(emptyEmp());
    setEditId(null);
  }

  function startEdit(e) {
    setForm({ ...e });
    setEditId(e.id);
    setManualOverrides({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleDelete(id) {
    if (!window.confirm("Delete this employee?")) return;
    updateState((prev) => ({
      ...prev,
      employees: prev.employees.filter((e) => e.id !== id),
    }));
  }

  const displayEmps = state.employees.filter(
    (e) =>
      (filterBranch === "all" || e.branchId === filterBranch) &&
      (filterStatus === "all" || e.status === filterStatus)
  );

  // Payroll KPIs
  const scope = state.employees.filter(
    (e) => e.status === "Active" && (isMain || e.branchId === selectedBranchId)
  );
  const totalPayroll = scope.reduce(
    (s, e) => s + e.monthlySalary + e.mealAllowance + e.otherAllowances,
    0
  );
  const totalDed = scope.reduce(
    (s, e) =>
      s +
      e.sssDeduction +
      e.philhealthDeduction +
      e.hdmfDeduction +
      e.otherDeductions,
    0
  );

  return (
    <div>
      {/* Payroll KPIs */}
      <div style={S.kpiGrid}>
        <KpiCard
          label="Active Employees"
          value={scope.length}
          color="#4f46e5"
          icon="👥"
        />
        <KpiCard
          label="Monthly Payroll"
          value={fmt(totalPayroll)}
          color="#16a34a"
          icon="💵"
        />
        <KpiCard
          label="Total Deductions"
          value={fmt(totalDed)}
          color="#d97706"
          icon="📊"
        />
        <KpiCard
          label="Net Payroll"
          value={fmt(totalPayroll - totalDed)}
          color="#7c3aed"
          icon="✅"
        />
      </div>

      {/* Form */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>
          {editId ? "✏️ Edit Employee" : "➕ Add Employee"}
        </h3>
        {error && <div style={S.errorMsg}>{error}</div>}
        <div style={S.formGrid}>
          {/* Basic info */}
          <div style={S.formGroup}>
            <label style={S.label}>Full Name *</label>
            <input
              value={form.name}
              onChange={(e) => setF("name", e.target.value)}
              style={S.input}
              placeholder="Employee Name"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Branch *</label>
            <select
              value={form.branchId}
              onChange={(e) => setF("branchId", e.target.value)}
              style={S.input}
            >
              <option value="">— Select —</option>
              {availBranches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Date Hired *</label>
            <input
              type="date"
              value={form.dateHired}
              onChange={(e) => setF("dateHired", e.target.value)}
              style={S.input}
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Date Separated</label>
            <input
              type="date"
              value={form.dateSeparated}
              onChange={(e) => setF("dateSeparated", e.target.value)}
              style={S.input}
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Status</label>
            <select
              value={form.status}
              onChange={(e) => setF("status", e.target.value)}
              style={S.input}
            >
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Payroll Schedule</label>
            <select
              value={form.payrollSchedule}
              onChange={(e) => setF("payrollSchedule", e.target.value)}
              style={S.input}
            >
              <option>Weekly</option>
              <option>Semi-monthly</option>
              <option>Monthly</option>
            </select>
          </div>
          {/* Compensation */}
          <div style={S.formGroup}>
            <label style={S.label}>Daily Salary (₱)</label>{" "}
            <input
              type="number"
              value={form.monthlySalary}
              onChange={(e) => setF("monthlySalary", e.target.value)}
              style={S.input}
              placeholder="0.00"
              min="0"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Meal Allowance (₱)</label>{" "}
            <input
              type="number"
              value={form.mealAllowance}
              onChange={(e) => setF("mealAllowance", e.target.value)}
              style={S.input}
              placeholder="0.00"
              min="0"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Other Allowances (₱)</label>{" "}
            <input
              type="number"
              value={form.otherAllowances}
              onChange={(e) => setF("otherAllowances", e.target.value)}
              style={S.input}
              placeholder="0.00"
              min="0"
            />
          </div>
          {/* Deductions */}
          <div style={S.formGroup}>
            <label style={S.label}>SSS Deduction (₱)</label>{" "}
            <input
              type="number"
              value={form.sssDeduction}
              onChange={(e) => setF("sssDeduction", e.target.value)}
              style={S.input}
              placeholder="0.00"
              min="0"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>PhilHealth (₱)</label>{" "}
            <input
              type="number"
              value={form.philhealthDeduction}
              onChange={(e) => setF("philhealthDeduction", e.target.value)}
              style={S.input}
              placeholder="0.00"
              min="0"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>HDMF (₱)</label>{" "}
            <input
              type="number"
              value={form.hdmfDeduction}
              onChange={(e) => setF("hdmfDeduction", e.target.value)}
              style={S.input}
              placeholder="0.00"
              min="0"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Other Deductions (₱)</label>{" "}
            <input
              type="number"
              value={form.otherDeductions}
              onChange={(e) => setF("otherDeductions", e.target.value)}
              style={S.input}
              placeholder="0.00"
              min="0"
            />
          </div>
                </div>

        {/* Government Contributions Section */}
       {Number(form.monthlySalary) > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{
              background: '#1e1b4b',
              color: 'white',
              padding: '10px 16px',
              borderRadius: '8px 8px 0 0',
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
           <span>🏛️ Government Contributions</span>
<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
  <span style={{ fontSize: 12, color: '#c7d2fe', fontWeight: 400 }}>
    Based on Monthly Salary: {fmt(Number(form.monthlySalary) * 26)}
    &nbsp;(Daily × 26)
  </span>
  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
    <div
      onClick={() => setF('govContribEnabled', !form.govContribEnabled)}
      style={{
        width: 40, height: 22, borderRadius: 11,
        background: form.govContribEnabled !== false ? '#4ade80' : '#6b7280',
        position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
      }}
    >
      <div style={{
        position: 'absolute', top: 3,
        left: form.govContribEnabled !== false ? 21 : 3,
        width: 16, height: 16, borderRadius: '50%',
        background: 'white', transition: 'left 0.2s',
      }}/>
    </div>
    <span style={{ fontSize: 12, color: '#c7d2fe' }}>
      {form.govContribEnabled !== false ? 'Enabled' : 'Disabled'}
    </span>
  </label>
</div>
            </div>
            <div style={{
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              opacity: form.govContribEnabled !== false ? 1 : 0.4,
              pointerEvents: form.govContribEnabled !== false ? 'auto' : 'none',
              borderRadius: '0 0 8px 8px',
              overflow: 'hidden',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>Contribution</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#0891b2', borderBottom: '1px solid #e2e8f0' }}>Employee Share</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#dc2626', borderBottom: '1px solid #e2e8f0' }}>Employer Share</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: '🔵 SSS',        empKey: 'sssEmployee',        emrKey: 'sssEmployer'        },
                    { label: '🟢 PhilHealth',  empKey: 'philhealthEmployee', emrKey: 'philhealthEmployer' },
                    { label: '🟠 HDMF',        empKey: 'hdmfEmployee',       emrKey: 'hdmfEmployer'       },
                  ].map((row, i) => (
                    <tr key={row.label} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b' }}>{row.label}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="number" min="0"
                          value={form[row.empKey] || ''}
                          onChange={e => setF(row.empKey, e.target.value)}
                          style={{ ...S.input, textAlign: 'right', width: '100%', boxSizing: 'border-box' }}
                          placeholder="0.00"
                        />
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="number" min="0"
                          value={form[row.emrKey] || ''}
                          onChange={e => setF(row.emrKey, e.target.value)}
                          style={{ ...S.input, textAlign: 'right', width: '100%', boxSizing: 'border-box' }}
                          placeholder="0.00"
                        />
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: '#1e1b4b' }}>
                    <td style={{ padding: '10px 12px', color: 'white', fontWeight: 700 }}>TOTAL</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#93c5fd', fontWeight: 700 }}>
                      {fmt((Number(form.sssEmployee)||0) + (Number(form.philhealthEmployee)||0) + (Number(form.hdmfEmployee)||0))}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#fca5a5', fontWeight: 700 }}>
                      {fmt((Number(form.sssEmployer)||0) + (Number(form.philhealthEmployer)||0) + (Number(form.hdmfEmployer)||0))}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ padding: '10px 14px', background: '#f0fdf4', fontSize: 12, color: '#065f46' }}>
                💡 Values are auto-computed based on 2026 rates. You can manually override any field.
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={handleSubmit} style={S.btnPrimary}>
            {editId ? "💾 Update" : "➕ Add Employee"}
          </button>
          {editId && (
            <button
              onClick={() => {
                setForm(emptyEmp());
                setEditId(null);
              }}
              style={S.btnSecondary}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={S.card}>
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3 style={{ ...S.cardTitle, margin: 0 }}>👥 Employee List</h3>
          <select
            value={filterBranch}
            onChange={(e) => setFilterBranch(e.target.value)}
            style={S.input}
          >
            <option value="all">All Branches</option>
            {availBranches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={S.input}
          >
            <option value="all">All Status</option>
            <option>Active</option>
            <option>Inactive</option>
          </select>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                {[
                  "Name",
                  "Branch",
                  "Status",
                  "Date Hired",
                  "Salary",
                  "Allowances",
                  "Gov't Contrib",
                  "Actions",
                ].map((h) => (
                  <th key={h} style={S.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayEmps.map((e) => {
                const branch = state.branches.find((b) => b.id === e.branchId);
                const allowances = e.mealAllowance + e.otherAllowances;
                const deductions =
                  e.sssDeduction +
                  e.philhealthDeduction +
                  e.hdmfDeduction +
                  e.otherDeductions;
                return (
                  <tr key={e.id} style={S.tr}>
                    <td style={S.td}>{e.name}</td>
                    <td style={S.td}>{branch?.name || e.branchId}</td>
                    <td style={S.td}>
                      <span
                        style={{
                          ...S.badge,
                          background:
                            e.status === "Active" ? "#dcfce7" : "#fee2e2",
                          color: e.status === "Active" ? "#16a34a" : "#dc2626",
                        }}
                      >
                        {e.status}
                      </span>
                    </td>
                    <td style={S.td}>{e.dateHired}</td>
                    <td style={S.td}>{fmt(e.monthlySalary)}</td>
                    <td style={S.td}>{fmt(allowances)}</td>
                 <td style={S.td}>
                      <span style={{
                        background: e.govContribEnabled !== false ? '#dcfce7' : '#fee2e2',
                        color: e.govContribEnabled !== false ? '#16a34a' : '#dc2626',
                        borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 600
                      }}>
                        {e.govContribEnabled !== false ? '✅ Active' : '❌ Disabled'}
                      </span>
                    </td>
                    <td style={S.td}>
                      <button onClick={() => startEdit(e)} style={S.btnTiny}>
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(e.id)}
                        style={{
                          ...S.btnTiny,
                          marginLeft: 4,
                          background: "#fee2e2",
                          color: "#dc2626",
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
              {displayEmps.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    style={{ ...S.td, textAlign: "center", color: "#9ca3af" }}
                  >
                    No employees found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── BACKUP & RESTORE TAB ────────────────────────────────────────────────────
function BackupRestoreTab({ state, updateState, setState }) {
  const fileRef = useRef(null);
  const csvFinRef = useRef(null);
  const csvEmpRef = useRef(null);
  const csvBrRef = useRef(null);
  const [preview, setPreview] = useState(null); // full JSON restore preview
  const [csvPreview, setCsvPreview] = useState(null); // CSV import preview
  const [msg, setMsg] = useState("");

  const flash = (text) => {
    setMsg(text);
    setTimeout(() => setMsg(""), 4000);
  };

  // ── A. Export full JSON backup
  function exportBackup() {
    const ds = new Date().toISOString().slice(0, 10);
    downloadFile(JSON.stringify(state, null, 2), `company-backup-${ds}.json`);
    flash("✅ Full backup exported.");
  }

  // ── B. Upload JSON for restore — validate then show preview
  function handleRestoreUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (!parsed.branches || !parsed.dailyRecords || !parsed.employees)
          return alert(
            "Invalid backup: missing required fields (branches / dailyRecords / employees)."
          );
        setPreview({
          data: parsed,
          summary: {
            branches: parsed.branches.length,
            employees: parsed.employees.length,
            records: parsed.dailyRecords.length,
          },
        });
      } catch {
        alert("Could not parse file. Ensure it is a valid JSON backup.");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  function confirmRestore() {
    setState({ ...preview.data, lastBackup: new Date().toISOString() });
    setPreview(null);
    flash("✅ Backup restored successfully.");
  }

  // ── C. CSV Export helpers
  function exportCSV(type) {
    const ds = new Date().toISOString().slice(0, 10);
    if (type === "records") {
      const rows = state.dailyRecords.map((r) => ({
        id: r.id,
        branchId: r.branchId,
        date: r.date,
        sales: r.sales,
        cost: r.cost,
        ...EXPENSE_FIELDS.reduce(
          (o, f) => ({ ...o, [f.key]: r.expenses[f.key] }),
          {}
        ),
      }));
      const fields = [
        { key: "id", label: "ID" },
        { key: "branchId", label: "Branch ID" },
        { key: "date", label: "Date" },
        { key: "sales", label: "Sales" },
        { key: "cost", label: "Cost" },
        ...EXPENSE_FIELDS.map((f) => ({ key: f.key, label: f.label })),
      ];
      downloadFile(
        objToCSV(rows, fields),
        `daily-records-${ds}.csv`,
        "text/csv"
      );
    } else if (type === "employees") {
      const fields = [
        "id",
        "name",
        "branchId",
        "dateHired",
        "dateSeparated",
        "status",
        "monthlySalary",
        "mealAllowance",
        "otherAllowances",
        "sssDeduction",
        "philhealthDeduction",
        "hdmfDeduction",
        "otherDeductions",
      ].map((k) => ({ key: k, label: k }));
      downloadFile(
        objToCSV(state.employees, fields),
        `employees-${ds}.csv`,
        "text/csv"
      );
    } else if (type === "branches") {
      downloadFile(
        objToCSV(state.branches, [
          { key: "id", label: "ID" },
          { key: "name", label: "Name" },
          { key: "isMain", label: "Is Main" },
        ]),
        `branches-${ds}.csv`,
        "text/csv"
      );
    } else if (type === "summary") {
      const rows = state.branches
        .filter((b) => !b.isMain)
        .map((b) => {
          const s = calcSummary(
            state.dailyRecords.filter((r) => r.branchId === b.id)
          );
          return {
            branch: b.name,
            totalSales: s.totalSales,
            totalCost: s.totalCost,
            totalExpenses: s.totalExpenses,
            netProfit: s.netProfit,
            grossMargin: s.grossMargin.toFixed(2) + "%",
            opExpRatio: s.opExpRatio.toFixed(2) + "%",
          };
        });
      const fields = [
        "branch",
        "totalSales",
        "totalCost",
        "totalExpenses",
        "netProfit",
        "grossMargin",
        "opExpRatio",
      ].map((k) => ({ key: k, label: k }));
      downloadFile(
        objToCSV(rows, fields),
        `dashboard-summary-${ds}.csv`,
        "text/csv"
      );
    }
    flash("✅ CSV exported.");
  }

  // ── D. CSV Import — parse then preview
  function handleCSVUpload(e, type) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const { headers, rows } = parseCSVRows(ev.target.result);
        const required =
          type === "records"
            ? ["branchId", "date", "sales", "cost"]
            : type === "employees"
            ? ["name", "branchId", "dateHired", "status"]
            : ["name"];
        const missing = required.filter((h) => !headers.includes(h));
        if (missing.length)
          return alert(`CSV missing required columns: ${missing.join(", ")}`);
        setCsvPreview({ type, headers, rows: rows.slice(0, 200) });
      } catch {
        alert("Failed to parse CSV.");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  function confirmCSVImport() {
    const { type, rows } = csvPreview;
    if (type === "records") {
      const newRecs = rows.map((r) => ({
        id: r.id || genId(),
        branchId: r.branchId,
        date: r.date,
        sales: Number(r.sales || 0),
        cost: Number(r.cost || 0),
        expenses: EXPENSE_FIELDS.reduce(
          (o, f) => ({ ...o, [f.key]: Number(r[f.key] || 0) }),
          {}
        ),
      }));
      updateState((prev) => {
        const keys = new Set(
          prev.dailyRecords.map((r) => `${r.branchId}_${r.date}`)
        );
        return {
          ...prev,
          dailyRecords: [
            ...prev.dailyRecords,
            ...newRecs.filter((r) => !keys.has(`${r.branchId}_${r.date}`)),
          ],
        };
      });
      flash(`✅ Daily records imported (duplicates skipped).`);
    } else if (type === "employees") {
      const newEmps = rows.map((r) => ({
        id: r.id || genId(),
        name: r.name,
        branchId: r.branchId,
        dateHired: r.dateHired,
        dateSeparated: r.dateSeparated || "",
        status: r.status || "Active",
        monthlySalary: Number(r.monthlySalary || 0),
        mealAllowance: Number(r.mealAllowance || 0),
        otherAllowances: Number(r.otherAllowances || 0),
        sssDeduction: Number(r.sssDeduction || 0),
        philhealthDeduction: Number(r.philhealthDeduction || 0),
        hdmfDeduction: Number(r.hdmfDeduction || 0),
        otherDeductions: Number(r.otherDeductions || 0),
      }));
      updateState((prev) => {
        const names = new Set(prev.employees.map((e) => e.name.toLowerCase()));
        return {
          ...prev,
          employees: [
            ...prev.employees,
            ...newEmps.filter((e) => !names.has(e.name.toLowerCase())),
          ],
        };
      });
      flash("✅ Employees imported (duplicates by name skipped).");
    } else if (type === "branches") {
      const newBrs = rows.map((r) => ({
        id: r.id || genId(),
        name: r.name,
        isMain: r.isMain === "true",
      }));
      updateState((prev) => {
        const names = new Set(prev.branches.map((b) => b.name.toLowerCase()));
        return {
          ...prev,
          branches: [
            ...prev.branches,
            ...newBrs.filter((b) => !names.has(b.name.toLowerCase())),
          ],
        };
      });
      flash("✅ Branches imported.");
    }
    setCsvPreview(null);
  }

  return (
    <div>
      {msg && <div style={S.successMsg}>{msg}</div>}

      {/* ── Restore Confirmation Modal ── */}
      {preview && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h3 style={{ marginTop: 0 }}>🔄 Restore Preview</h3>
            <p style={S.helpText}>
              This will <strong>replace all current data</strong>. Please
              confirm:
            </p>
            <table style={S.table}>
              <tbody>
                <tr>
                  <td style={S.td}>
                    <strong>Branches</strong>
                  </td>{" "}
                  <td style={S.td}>{preview.summary.branches}</td>
                </tr>
                <tr>
                  <td style={S.td}>
                    <strong>Employees</strong>
                  </td>{" "}
                  <td style={S.td}>{preview.summary.employees}</td>
                </tr>
                <tr>
                  <td style={S.td}>
                    <strong>Daily Records</strong>
                  </td>{" "}
                  <td style={S.td}>{preview.summary.records}</td>
                </tr>
              </tbody>
            </table>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={confirmRestore} style={S.btnPrimary}>
                ✅ Confirm Restore
              </button>
              <button onClick={() => setPreview(null)} style={S.btnSecondary}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV Preview Modal ── */}
      {csvPreview && (
        <div style={S.modal}>
          <div style={{ ...S.modalBox, maxWidth: 820, width: "92%" }}>
            <h3 style={{ marginTop: 0 }}>
              📋 CSV Import Preview — {csvPreview.type}
            </h3>
            <p style={S.helpText}>
              {csvPreview.rows.length} rows detected. Showing first 10:
            </p>
            <div
              style={{
                overflowX: "auto",
                maxHeight: 280,
                overflowY: "auto",
                marginBottom: 16,
              }}
            >
              <table style={S.table}>
                <thead>
                  <tr>
                    {csvPreview.headers.slice(0, 8).map((h) => (
                      <th key={h} style={S.th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.rows.slice(0, 10).map((r, i) => (
                    <tr key={i} style={S.tr}>
                      {csvPreview.headers.slice(0, 8).map((h) => (
                        <td key={h} style={S.td}>
                          {r[h]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={confirmCSVImport} style={S.btnPrimary}>
                ✅ Import
              </button>
              <button
                onClick={() => setCsvPreview(null)}
                style={S.btnSecondary}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))",
          gap: 20,
        }}
      >
        {/* Full Backup (JSON) */}
        <div style={S.card}>
          <h3 style={S.cardTitle}>📦 Full Backup (JSON)</h3>
          <p style={S.helpText}>
            Export the entire database as a single JSON file. This is the safest
            way to preserve all data.
          </p>
          {state.lastBackup && (
            <p style={S.helpText}>
              Last auto-save:{" "}
              <strong>{new Date(state.lastBackup).toLocaleString()}</strong>
            </p>
          )}
          <button onClick={exportBackup} style={S.btnPrimary}>
            ⬇️ Export Full Backup
          </button>
          <hr
            style={{
              margin: "16px 0",
              border: "none",
              borderTop: "1px solid #e5e7eb",
            }}
          />
          <h4 style={{ margin: "0 0 8px" }}>🔄 Restore from Backup</h4>
          <p style={S.helpText}>
            Upload a previously exported JSON file to fully restore all data.
          </p>
          <input
            type="file"
            ref={fileRef}
            accept=".json"
            onChange={handleRestoreUpload}
            style={{ display: "none" }}
          />
          <button
            onClick={() => fileRef.current.click()}
            style={S.btnSecondary}
          >
            ⬆️ Upload Backup File
          </button>
        </div>

        {/* CSV Export */}
        <div style={S.card}>
          <h3 style={S.cardTitle}>📄 CSV Export</h3>
          <p style={S.helpText}>
            Export data as CSV for use in Excel or other spreadsheet tools.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={() => exportCSV("summary")} style={S.btnOutline}>
              📊 Export Dashboard Summary
            </button>
            <button onClick={() => exportCSV("records")} style={S.btnOutline}>
              📋 Export Daily Records
            </button>
            <button onClick={() => exportCSV("employees")} style={S.btnOutline}>
              👥 Export Employee List
            </button>
            <button onClick={() => exportCSV("branches")} style={S.btnOutline}>
              🏪 Export Branches
            </button>
          </div>
        </div>

        {/* CSV Import */}
        <div style={S.card}>
          <h3 style={S.cardTitle}>📥 CSV Import</h3>
          <p style={S.helpText}>
            Import from CSV. A preview is shown before data is saved. Duplicates
            are skipped automatically.
          </p>
          <input
            type="file"
            ref={csvFinRef}
            accept=".csv"
            onChange={(e) => handleCSVUpload(e, "records")}
            style={{ display: "none" }}
          />
          <input
            type="file"
            ref={csvEmpRef}
            accept=".csv"
            onChange={(e) => handleCSVUpload(e, "employees")}
            style={{ display: "none" }}
          />
          <input
            type="file"
            ref={csvBrRef}
            accept=".csv"
            onChange={(e) => handleCSVUpload(e, "branches")}
            style={{ display: "none" }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              onClick={() => csvFinRef.current.click()}
              style={S.btnOutline}
            >
              📋 Import Daily Records (CSV)
            </button>
            <button
              onClick={() => csvEmpRef.current.click()}
              style={S.btnOutline}
            >
              👥 Import Employees (CSV)
            </button>
            <button
              onClick={() => csvBrRef.current.click()}
              style={S.btnOutline}
            >
              🏪 Import Branches (CSV)
            </button>
          </div>
          <p style={{ ...S.helpText, marginTop: 12 }}>
            <strong>Required CSV columns:</strong>
            <br />
            Records: branchId, date, sales, cost
            <br />
            Employees: name, branchId, dateHired, status
            <br />
            Branches: name
          </p>
        </div>

        {/* Data Stats + Danger Zone */}
        <div style={S.card}>
          <h3 style={S.cardTitle}>📊 Current Data Stats</h3>
          <table style={S.table}>
            <tbody>
              <tr style={S.tr}>
                <td style={S.td}>Branches</td>{" "}
                <td style={S.td}>
                  <strong>{state.branches.length}</strong>
                </td>
              </tr>
              <tr style={S.tr}>
                <td style={S.td}>Employees</td>{" "}
                <td style={S.td}>
                  <strong>{state.employees.length}</strong>
                </td>
              </tr>
              <tr style={S.tr}>
                <td style={S.td}>Active Employees</td>{" "}
                <td style={S.td}>
                  <strong>
                    {
                      state.employees.filter((e) => e.status === "Active")
                        .length
                    }
                  </strong>
                </td>
              </tr>
              <tr style={S.tr}>
                <td style={S.td}>Daily Records</td>{" "}
                <td style={S.td}>
                  <strong>{state.dailyRecords.length}</strong>
                </td>
              </tr>
            </tbody>
          </table>
          <hr
            style={{
              margin: "16px 0",
              border: "none",
              borderTop: "1px solid #e5e7eb",
            }}
          />
          <h4 style={{ margin: "0 0 8px", color: "#dc2626" }}>
            ⚠️ Danger Zone
          </h4>
          <p style={S.helpText}>
            Reset all data back to the built-in sample dataset. This cannot be
            undone.
          </p>
          <button
           onClick={async () => {
  // Step 1 — First warning
  const firstConfirm = window.confirm(
    "⚠️ WARNING: This will permanently wipe ALL data including branches, employees, and daily records.\n\nA backup will be automatically downloaded before wiping.\n\nAre you sure you want to continue?"
  );
  if (!firstConfirm) return;

  // Step 2 — Second warning
  const secondConfirm = window.confirm(
    "🔴 FINAL WARNING: This action CANNOT be undone.\n\nYour current data will be downloaded as a backup file first.\n\nClick OK to download backup and wipe all data."
  );
  if (!secondConfirm) return;

  // Step 3 — Auto download backup before wiping
  const backupData = {
    branches: state.branches,
    employees: state.employees,
    dailyRecords: state.dailyRecords,
    lastBackup: new Date().toISOString(),
  };
  const ds = new Date().toISOString().slice(0, 10);
  downloadFile(
    JSON.stringify(backupData, null, 2),
    `excelsior-backup-before-wipe-${ds}.json`
  );

  // Step 4 — Wipe everything
  const blank = {
    branches: [],
    employees: [],
    dailyRecords: [],
    lastBackup: new Date().toISOString(),
  };
  setState(blank);
  await supabase
    .from('dashboard_data')
    .upsert({
      user_id: session.user.id,
      branches: [],
      employees: [],
      daily_records: [],
      last_saved: new Date().toISOString(),
    }, { onConflict: 'user_id' });

  alert("✅ Data wiped successfully. Your backup has been downloaded to your computer.");
}}

            style={{
              ...S.btnOutline,
              color: "#dc2626",
              borderColor: "#dc2626",
            }}
          >
            🗑️ Wipe All Data
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── BRANCH MANAGER MODAL ────────────────────────────────────────────────────
function BranchManager({ state, updateState, onClose, setSelectedBranchId, branchLimit }) {
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [error, setError] = useState("");
  function handleAdd() {
    const t = name.trim();
    if (!t) return setError("Branch name is required.");
    if (state.branches.filter(b => !b.isMain).length >= branchLimit) {
      return setError(`Your plan allows a maximum of ${branchLimit} branch${branchLimit === 1 ? '' : 'es'}. Please upgrade your plan to add more.`);
    }

    if (state.branches.some((b) => b.name.toLowerCase() === t.toLowerCase()))
      return setError("A branch with this name already exists.");
    updateState((prev) => ({
      ...prev,
      branches: [...prev.branches, { id: genId(), name: t, isMain: false }],
    }));
    setName("");
    setError("");
  }

  function handleUpdate(id) {
    const t = editName.trim();
    if (!t) return;
    updateState((prev) => ({
      ...prev,
      branches: prev.branches.map((b) => (b.id === id ? { ...b, name: t } : b)),
    }));
    setEditId(null);
  }

  function handleDelete(id) {
    if (
      !window.confirm(
        "Delete branch? Associated records/employees remain but will be unlinked."
      )
    )
      return;
    updateState((prev) => ({
      ...prev,
      branches: prev.branches.filter((b) => b.id !== id),
    }));
    setSelectedBranchId("main");
  }

  return (
    <div style={S.modal}>
      <div style={S.modalBox}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0 }}>⚙️ Branch Management</h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              cursor: "pointer",
              color: "#6b7280",
            }}
          >
            ✕
          </button>
        </div>

        {error && <div style={S.errorMsg}>{error}</div>}

        {/* Add new branch */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            style={{ ...S.input, flex: 1 }}
            placeholder="New branch name"
          />
          <button onClick={handleAdd} style={S.btnPrimary}>
            ➕ Add
          </button>
        </div>

        {/* Branch list */}
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Name</th>
              <th style={S.th}>Type</th>
              <th style={S.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {state.branches.map((b) => (
              <tr key={b.id} style={S.tr}>
                <td style={S.td}>
                  {editId === b.id ? (
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      style={{ ...S.input, padding: "4px 8px" }}
                      autoFocus
                    />
                  ) : (
                    b.name
                  )}
                </td>
                <td style={S.td}>{b.isMain ? "🏢 Main" : "🏪 Branch"}</td>
                <td style={S.td}>
                  {!b.isMain &&
                    (editId === b.id ? (
                      <>
                        <button
                          onClick={() => handleUpdate(b.id)}
                          style={S.btnTiny}
                        >
                          💾
                        </button>
                        <button
                          onClick={() => setEditId(null)}
                          style={{ ...S.btnTiny, marginLeft: 4 }}
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setEditId(b.id);
                            setEditName(b.name);
                          }}
                          style={S.btnTiny}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(b.id)}
                          style={{
                            ...S.btnTiny,
                            marginLeft: 4,
                            background: "#fee2e2",
                            color: "#dc2626",
                          }}
                        >
                          🗑️
                        </button>
                      </>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button onClick={onClose} style={{ ...S.btnSecondary, marginTop: 16 }}>
          Close
        </button>
      </div>
    </div>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const S = {
layout: {
  display: "flex",
  minHeight: "100vh",
  background: "#f8fafc"
},

sidebar: {
background: "#1e293b",
color: "#ffffff",
display: "flex",
flexDirection: "column"
},

sidebarTitle: {
  fontSize: 18,
  fontWeight: 700,
  marginBottom: 24
},

menuItem: {
  padding: "10px 12px",
  borderRadius: 8,
  marginBottom: 6,
  cursor: "pointer",
  transition: "background 0.2s ease"
},

content: {
flex: 1,
padding: 28,
maxWidth: "1400px",
margin: "0 auto"
},

header: {
  background: "#ffffff",
  padding: "14px 20px",
  borderRadius: 12,
  marginBottom: 20,
  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
},

headerTitle: {
  fontSize: 20,
  fontWeight: 600,
  color: "#1e293b"
},

cardGrid: {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
  gap: 16,
  marginBottom: 20
},
  app: {
    fontFamily: "'Segoe UI',Arial,sans-serif",
    background: "#f3f4f6",
    minHeight: "100vh",
    color: "#111827",
  },
  header: {
    background: "#1e1b4b",
    color: "#fff",
    padding: "12px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  headerRight: { display: "flex", alignItems: "center", gap: 8 },
  logo: { fontSize: 20, fontWeight: 700 },
  branchSel: {
    background: "#312e81",
    color: "#fff",
    border: "1px solid #4f46e5",
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 14,
    cursor: "pointer",
  },
  savedBadge: {
    background: "#312e81",
    color: "#c7d2fe",
    fontSize: 12,
    padding: "4px 10px",
    borderRadius: 20,
    whiteSpace: "nowrap",
  },
  nav: {
    background: "#fff",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    overflowX: "auto",
  },
  navBtn: {
    background: "none",
    border: "none",
    padding: "14px 20px",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: 14,
    color: "#6b7280",
    borderBottom: "3px solid transparent",
    whiteSpace: "nowrap",
  },
  navBtnActive: { color: "#4f46e5", borderBottom: "3px solid #4f46e5" },
  main: { maxWidth: 1200, margin: "0 auto", padding: 20 },
  card: {
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    boxShadow: "0 1px 3px rgba(0,0,0,.1)",
  },
  cardTitle: {
    margin: "0 0 16px",
    fontSize: 16,
    fontWeight: 700,
    color: "#1f2937",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(155px,1fr))",
    gap: 16,
    marginBottom: 20,
  },
  kpiCard: {
    background: "#fff",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 1px 3px rgba(0,0,0,.1)",
    textAlign: "center",
  },
  kpiIcon: { fontSize: 22, marginBottom: 4 },
  kpiLabel: {
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 4,
    fontWeight: 500,
  },
  kpiValue: { fontSize: 17, fontWeight: 700 },
  chartCard: {
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    boxShadow: "0 1px 3px rgba(0,0,0,.1)",
  },
  chartTitle: {
    margin: "0 0 16px",
    fontSize: 15,
    fontWeight: 700,
    color: "#1f2937",
  },
  filterRow: {
    background: "#fff",
    borderRadius: 12,
    padding: "12px 20px",
    marginBottom: 20,
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
    boxShadow: "0 1px 3px rgba(0,0,0,.08)",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
    gap: 12,
    marginBottom: 8,
  },
  formGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: "#374151" },
  input: {
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 14,
    outline: "none",
    background: "#f9fafb",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    background: "#f9fafb",
    padding: "10px 12px",
    textAlign: "left",
    fontWeight: 600,
    borderBottom: "2px solid #e5e7eb",
    whiteSpace: "nowrap",
    color: "#374151",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #f3f4f6",
    verticalAlign: "middle",
  },
  tr: { transition: "background .1s" },
  badge: {
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
  },
  btnPrimary: {
    background: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 20px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
  },
  btnSecondary: {
    background: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "10px 20px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
  },
  btnOutline: {
    background: "none",
    color: "#4f46e5",
    border: "1px solid #4f46e5",
    borderRadius: 8,
    padding: "10px 20px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
  },
  btnSmall: {
    background: "#312e81",
    color: "#c7d2fe",
    border: "none",
    borderRadius: 6,
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
  btnTiny: {
    background: "#f3f4f6",
    color: "#374151",
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    padding: "4px 8px",
    cursor: "pointer",
    fontSize: 13,
  },
  errorMsg: {
    background: "#fee2e2",
    color: "#dc2626",
    padding: "10px 14px",
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 14,
  },
  successMsg: {
    background: "#dcfce7",
    color: "#16a34a",
    padding: "10px 14px",
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 14,
    fontWeight: 600,
  },
  helpText: {
    color: "#6b7280",
    fontSize: 13,
    margin: "0 0 12px",
    lineHeight: 1.5,
  },
  modal: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  modalBox: {
    background: "#fff",
    borderRadius: 16,
    padding: 28,
    maxWidth: 500,
    width: "100%",
    boxShadow: "0 20px 60px rgba(0,0,0,.3)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
};
